// Decorative, procedural illustrations; no video, external assets, or physics engine.
// Cache quantized frames so the shared lifecycle can pause and resume cheaply.
const point = ([x,y,z], small) => {
    const yaw = -.13, pitch = .08;
    const xx = x*Math.cos(yaw)+z*Math.sin(yaw), zz = -x*Math.sin(yaw)+z*Math.cos(yaw);
    const yy = y*Math.cos(pitch)-zz*Math.sin(pitch), depth = y*Math.sin(pitch)+zz*Math.cos(pitch);
    const scale = (small ? .89 : 1.12) * 1600/(1600+depth);
    return [(small ? 380 : 600)+xx*scale, (small ? 300 : 230)+yy*scale];
};
const xy = p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`;
const line = points => points.map((p,i)=>(i?'L':'M')+xy(p)).join('');
function cachedScene(draw) {
    let previousKey, path;
    return {labels:[], draw(seconds,small,reduced=false) {
        const t = reduced ? 1.7 : Math.floor(seconds*(small?24:30))/(small?24:30);
        const key = `${t}/${small}`;
        if(key!==previousKey) { path=draw(t,small);previousKey=key; }
        return {path,labels:[]};
    }};
}

// A physical LED wall: the grid never moves. Moving depth silhouettes switch
// its lamps on; several delayed exposures leave monochrome afterimages.
export function createDancingChapterScene() {
    const layers = [
        { opacity: .11, strokeWidth: 2.3, overlap: false },
        { opacity: .16 }, { opacity: .28 }, { opacity: .43 },
        { opacity: .62 }, { opacity: .8 },
    ];
    const layouts = new Map(), delays = [0,.19,.42,.73,1.12,1.6];
    let previousKey, previousFrame;
    function layout(small) {
        if (layouts.has(small)) return layouts.get(small);
        const cols=small?52:74, rows=small?36:26, spacing=small?13:14;
        const left=(small?380:600)-(cols-1)*spacing/2, top=(small?300:230)-(rows-1)*spacing/2;
        const dots=Array.from({length:cols*rows},(_,i)=>'M'+xy([left+(i%cols)*spacing,top+Math.floor(i/cols)*spacing])+'l.05,0');
        const result={cols,rows,dots,grid:dots.join(''),masks:delays.map(()=>new Uint8Array(cols*rows))};
        layouts.set(small,result);return result;
    }
    function silhouette(mask, grid, t, small) {
        mask.fill(0);
        const {cols,rows}=grid, scale=small?1.25:1, count=small?2:3;
        const stamp=(x0,y0,x1,y1,inside)=>{
            for(let y=Math.max(0,Math.ceil(y0));y<=Math.min(rows-1,Math.floor(y1));y++)
                for(let x=Math.max(0,Math.ceil(x0));x<=Math.min(cols-1,Math.floor(x1));x++)
                    if(inside(x,y))mask[y*cols+x]=1;
        };
        const ellipse=(x,y,rx,ry)=>stamp(x-rx,y-ry,x+rx,y+ry,(a,b)=>((a-x)/rx)**2+((b-y)/ry)**2<=1);
        const limb=(a,b,r)=>{
            const dx=b[0]-a[0],dy=b[1]-a[1],length=dx*dx+dy*dy;
            stamp(Math.min(a[0],b[0])-r,Math.min(a[1],b[1])-r,Math.max(a[0],b[0])+r,Math.max(a[1],b[1])+r,(x,y)=>{
                const f=Math.max(0,Math.min(1,((x-a[0])*dx+(y-a[1])*dy)/(length||1)));
                return (x-a[0]-f*dx)**2+(y-a[1]-f*dy)**2<=r*r;
            });
        };
        for(let person=0;person<count;person++) {
            const phase=t*.85+person*2.1, wave=Math.sin(phase), sway=Math.sin(t*.43+person*2.4)*2.9;
            const origin=(small?14.5+person*23:13+person*23.5)+sway;
            const lift=.35*Math.sin(phase*1.7), lean=.7*Math.sin(phase*.8);
            const p=(x,y)=>[origin+x*scale,(small?2:0)+(y+lift)*scale];
            const head=p(lean,5.4), chest=p(lean,10.3), hips=p(0,15);
            ellipse(...head,1.55*scale,1.9*scale);
            limb(p(lean,7),chest,1.15*scale);
            ellipse(chest[0],(chest[1]+hips[1])/2,2.65*scale,4.25*scale);
            for(const side of [-1,1]) {
                const reach=Math.sin(phase+side*.9), shoulder=p(lean+side*2.45,9.3);
                const elbow=p(side*(5.5+1.2*Math.cos(phase)),8.5+3*reach);
                const hand=p(side*(7.4+1.3*Math.cos(phase*.7)),5.9+4.4*reach);
                limb(shoulder,elbow,1.05*scale);limb(elbow,hand,.85*scale);ellipse(...hand,.95*scale,1.15*scale);
                const knee=p(side*2.1+wave*.7,19.7), foot=p(side*(3+Math.max(0,side*wave)*1.4),24-Math.max(0,side*wave)*1.1);
                limb(p(side*1.3,15),knee,1.25*scale);limb(knee,foot,.95*scale);limb(foot,[foot[0]+side*1.2*scale,foot[1]],.8*scale);
            }
        }
    }
    return {labels:[],layers,draw(seconds,small,reduced=false) {
        const fps=small?20:24,t=reduced?1.7:Math.floor(seconds*fps)/fps,key=`${t}/${small}`;
        if(key===previousKey)return previousFrame;
        const grid=layout(small),buckets=delays.map(()=>[]);
        delays.forEach((delay,i)=>silhouette(grid.masks[i],grid,t-delay,small));
        for(let i=0;i<grid.dots.length;i++) {
            // A lamp belongs to its newest exposure, avoiding stacked brightness.
            const exposure=grid.masks.findIndex(mask=>mask[i]);
            if(exposure>=0)buckets[exposure].push(grid.dots[i]);
        }
        previousKey=key;
        previousFrame={path:buckets[0].join(''),labels:[],layers:[grid.grid,...buckets.slice(1).reverse().map(dots=>dots.join(''))]};
        return previousFrame;
    }};
}

// A Comfy-style graph: connected ports, parameter rows, previews and travelling
// packets. It evokes the tooling, rather than claiming a specific production graph.
export function createWorkflowChapterScene() {
    const nodes=[[-405,-115],[-405,95],[-190,-100],[-190,115],[40,-90],[40,125],[265,-110],[265,105],[430,0]];
    const edges=[[0,2],[1,3],[2,4],[3,4],[3,5],[4,6],[5,7],[6,8],[7,8]];
    return cachedScene((t,small)=>{
        let path='';
        const project=p=>point(small?[p[0]*.64,p[1]*1.3,p[2]]:p,small);
        const position=i=>[...nodes[i],Math.sin(t*.35+i*.7)*20];
        const dot=(p,r=3)=>{const [x,y]=project(p);path+=line([[x-r,y],[x,y-r],[x+r,y],[x,y+r],[x-r,y]]);};
        nodes.forEach((_,i)=>{
            const [x,y,z]=position(i),w=120,h=104;
            const rect=(left,top,width,height,depth)=>line([[left,top,depth],[left+width,top,depth],[left+width,top+height,depth],[left,top+height,depth],[left,top,depth]].map(project));
            path+=rect(x-w/2,y-h/2,w,h,z)+rect(x-w/2,y-h/2,w,h,z+12);
            path+=line([[x-w/2,y-28,z],[x+w/2,y-28,z]].map(project));
            for(let row=0;row<3;row++)path+=rect(x-39,y-15+row*17,78,9,z);
            dot([x-w/2,y-33,z],4);dot([x+w/2,y-33,z],4);
        });
        for(const [index,[a,b]] of edges.entries()) {
            const from=position(a),to=position(b);from[0]+=60;to[0]-=60;from[1]-=33;to[1]-=33;
            const control1=[from[0]+60,from[1],from[2]],control2=[to[0]-60,to[1],to[2]];
            const curve=f=>from.map((v,i)=>(1-f)**3*v+3*(1-f)**2*f*control1[i]+3*(1-f)*f*f*control2[i]+f**3*to[i]);
            path+=line(Array.from({length:19},(_,i)=>project(curve(i/18))));
            dot(curve((t*.16+index*.13)%1),4);
        }
        return path;
    });
}
